import React, { useCallback, useMemo, useState } from "react";
import { Platform, View } from "react-native";
import { DisplayableDocument } from "../../contexts/DocumentManager";
import VStack from "../../ui_atoms/VStack";
import Loading from "../Loading";
import TextField from "../TextField";
import { useI18n } from "../../hooks/i18n-hooks";
import Button from "../Button";
import * as FileSystem from "expo-file-system";
import * as Sharing from "expo-sharing";
import { customAlphabet } from "nanoid";
import DocumentViewer from "./DocumentViewer";
import { useSetAlert } from "../../hooks/use-set-alert";
import ScreenHeader from "../../screens/user/ScreenHeader";
import Gap from "../../ui_atoms/Gap";

const generateShortId = customAlphabet("0123456789", 5);

// Download URLs carry a query string (Firebase: "…/o/users%2F…%2Fx_MISSION_ANNEX.csv?alt=media&token=…"),
// so the extension is read from the path only.
const getUrlFileExtension = (url: string) =>
    url
        .split(/[?#]/)[0]
        .split("/")
        .pop()
        ?.match(/\.([a-z0-9]+)$/i)?.[1]
        .toLowerCase() ?? null;

export type DocumentEditorProps = {
    onSave?: (document: DisplayableDocument) => void | Promise<void>;
    onClose: () => void;
    onRemove?: (document: DisplayableDocument) => void;
    document?: DisplayableDocument;
    mimeType?: string;
    processing?: boolean;
    fixedName?: string;
    saveButtonTitle?: string;
};

const DocumentEditor: React.FC<DocumentEditorProps> = ({
    onSave,
    onClose,
    onRemove,
    document,
    mimeType,
    processing,
    fixedName,
    saveButtonTitle,
}) => {
    const i18n = useI18n();
    const { setAlert } = useSetAlert();

    const [documentTitle, setDocumentTitle] = useState<string>(document?.title ?? "");
    const [documentInlineContent, setDocumentInlineContent] = useState<string | undefined>(
        document?.inlineContent ?? undefined,
    );
    const isInlineContent = document?.inlineContent != null;
    // Resync local edits whenever the parent provides a new `document`. Uses the
    // "adjust state on prop change" pattern from
    // https://react.dev/learn/you-might-not-need-an-effect
    const [prevDocument, setPrevDocument] = useState(document);
    if (document !== prevDocument) {
        setPrevDocument(document);
        setDocumentTitle(document?.title ?? "");
        setDocumentInlineContent(document?.inlineContent ?? undefined);
    }

    const shareDocument = useCallback(() => {
        const fn = async () => {
            if (document?.documentUrl) {
                if (Platform.OS === "web") {
                    // Web-specific implementation
                    const link = window.document.createElement("a");
                    link.href = document.documentUrl;
                    link.download = documentTitle || "document";
                    link.target = "_blank";
                    window.document.body.appendChild(link);
                    link.click();
                    window.document.body.removeChild(link);
                } else {
                    const ext = (() => {
                        switch (mimeType) {
                            case "application/pdf":
                                return "pdf";
                            default:
                                return getUrlFileExtension(document.documentUrl) || "bin";
                        }
                    })();
                    const randomFileId = generateShortId();
                    const baseName = (documentTitle || `file-${randomFileId}`).replaceAll(" ", "_");
                    // The title defaults to the picked file's name, which already has its extension.
                    const fileName = baseName.toLowerCase().endsWith(`.${ext}`)
                        ? baseName
                        : `${baseName}.${ext}`;
                    const localUri = new FileSystem.File(FileSystem.Paths.cache, fileName);
                    const downloadRet = await FileSystem.File.downloadFileAsync(
                        document.documentUrl || "",
                        localUri,
                    );
                    await Sharing.shareAsync(downloadRet.uri);
                }
            }
        };
        void fn();
    }, [document?.documentUrl, documentTitle, mimeType]);

    const doSaveDocument = React.useCallback(() => {
        if (documentTitle.length === 0) return;

        if (document) {
            void onSave?.({
                ...document,
                title: documentTitle,
                inlineContent: isInlineContent ? (documentInlineContent ?? null) : null,
            });
        }
    }, [document, documentTitle, onSave, isInlineContent, documentInlineContent]);

    const doRemove = React.useCallback(() => {
        if (onRemove && document) {
            setAlert({
                title: i18n.t("components.document.remove_document_title"),
                message: i18n.t("components.document.remove_document_description"),
                buttons: [
                    {
                        text: i18n.t("components.document.remove_document_confirm"),
                        onPress: () => onRemove(document),
                        style: "destructive",
                    },
                    {
                        text: i18n.t("components.document.remove_document_cancel"),
                        style: "cancel",
                    },
                ],
                options: { cancelable: true },
            });
        }
    }, [document, i18n, onRemove, setAlert]);

    const doClose = React.useCallback(() => {
        onClose();
    }, [onClose]);

    const doShare = React.useCallback(() => {
        shareDocument();
    }, [shareDocument]);

    const screenTitle = useMemo(() => {
        if (isInlineContent) {
            return fixedName || documentTitle || i18n.t("components.document.view_inline_content");
        }
        return fixedName || documentTitle || " ";
    }, [isInlineContent, i18n, fixedName, documentTitle]);

    const assetDocumentDisplay = useMemo(() => {
        return (
            <>
                {document?.documentUrl ? (
                    <DocumentViewer
                        documentUrl={document.documentUrl}
                        knownMimeType={mimeType}
                        fileName={document.title}
                    />
                ) : (
                    <Loading />
                )}
                {onSave && (
                    <VStack v={1}>
                        {!fixedName && (
                            <TextField
                                autoFocus
                                style={{ alignSelf: "stretch" }}
                                label={i18n.t("components.document.title_label")}
                                placeholder={i18n.t("components.document.title_placeholder")}
                                value={documentTitle}
                                onChangeText={setDocumentTitle}
                                disabled={processing}
                                withIosDoneButton
                            />
                        )}
                    </VStack>
                )}
            </>
        );
    }, [
        document?.documentUrl,
        document?.title,
        mimeType,
        onSave,
        fixedName,
        documentTitle,
        processing,
        i18n,
    ]);

    const inlineDisplay = useMemo(() => {
        return (
            <>
                <VStack h="2/3" gap={1}>
                    <TextField
                        autoFocus
                        style={{ alignSelf: "stretch" }}
                        label={i18n.t("components.document.title_label")}
                        value={documentTitle}
                        onChangeText={setDocumentTitle}
                        disabled={processing || !onSave}
                    />
                    <TextField
                        multiline
                        style={{ alignSelf: "stretch" }}
                        label={i18n.t("components.document.inline_content_label")}
                        value={documentInlineContent || ""}
                        onChangeText={setDocumentInlineContent}
                        disabled={processing || !onSave}
                    />
                </VStack>
                <Gap mode="vertical" grow />
            </>
        );
    }, [
        documentTitle,
        documentInlineContent,
        setDocumentTitle,
        setDocumentInlineContent,
        processing,
        i18n,
        onSave,
    ]);

    return (
        <View style={{ flex: 1, backgroundColor: "white" }}>
            <ScreenHeader
                label={screenTitle}
                useSmallLabel
                // The editor is shown inside a presented native modal on iOS (already inset from the
                // top), so don't add the device top inset a second time.
                disableTopInset={Platform.OS === "ios"}
                leftIcon="close"
                onLeftAction={doClose}
                actions={[
                    ...(!isInlineContent
                        ? [
                              {
                                  label: i18n.t("components.document.share"),
                                  icon: "share" as const,
                                  onPress: doShare,
                              },
                          ]
                        : []),
                    ...(onRemove
                        ? [
                              {
                                  label: i18n.t("delete"),
                                  icon: "trash" as const,
                                  color: "red",
                                  onPress: doRemove,
                              },
                          ]
                        : []),
                ]}
            />
            <VStack h={onSave ? "2/3" : undefined} style={{ flex: 1 }}>
                {!isInlineContent && assetDocumentDisplay}
                {isInlineContent && inlineDisplay}
                {onSave && (
                    <>
                        <VStack h="2/3">
                            <Button
                                testID="save-document"
                                size="large"
                                style={{ alignSelf: "stretch" }}
                                buttonMode="secondary"
                                label={saveButtonTitle || i18n.t("save")}
                                onPress={doSaveDocument}
                                processing={processing}
                                disabled={
                                    (!fixedName && documentTitle.length === 0) ||
                                    (!isInlineContent && !document?.documentUrl) ||
                                    (isInlineContent && !documentInlineContent)
                                }
                            />
                        </VStack>
                        <Gap mode="vertical" gap={1} />
                    </>
                )}
            </VStack>
        </View>
    );
};

export default DocumentEditor;
